import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckIcon from '@mui/icons-material/Check';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Fade,
    FormControlLabel,
    FormGroup,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import api from '../../api';
import { listFarmLinkSupportedPrinterModels } from '../../grpc/printerModelsApi';
import { patchStoredUser } from '../../helpers/authSession';
import { formatCurrencyInCents } from '../../helpers/formatters';
import { fetchFeatureFlags } from '../../helpers/featureFlags';
import {
    findPlanQuota,
    formatPlanLimit,
    planLimitFor,
    planQuotaPeriodLabel,
} from '../../helpers/planLimits';
import { isProSubscriptionPlan, isUnlimitedSubscriptionPlan } from '../../helpers/subscription';

export const MIGRATION_CHECKBOX_KEYS = ['monthly_charge', 'delinquency', 'billing_channels'];

/** No plano Ilimitado exigimos um aceite a mais sobre capacidade de infraestrutura. */
const UNLIMITED_CAPACITY_KEY = 'unlimited_capacity';

function migrationCheckboxKeysFor(plan) {
    return isUnlimitedSubscriptionPlan(plan)
        ? [...MIGRATION_CHECKBOX_KEYS, UNLIMITED_CAPACITY_KEY]
        : MIGRATION_CHECKBOX_KEYS;
}

export const UNLIMITED_CAPACITY_EXPLANATION = `No plano Ilimitado não aplicamos nenhum limite de quantidade à sua conta: você cadastra quantas impressoras, vendas, impressões, filamentos e demais registros precisar, sem bloqueio por volume.

Esse compromisso é sobre <strong>limitação de uso</strong>, e não sobre capacidade de infraestrutura. A plataforma funciona em servidores com processamento, memória e armazenamento finitos e compartilhados entre os assinantes. Em cenários de volume muito acima do normal, ou em momentos de pico, pode haver alguma oscilação de desempenho, como um tempo maior para gerar relatórios e importações.

Se isso acontecer, o dimensionamento dos recursos computacionais necessários será conduzido com a maior celeridade possível. Vale considerar que ampliar infraestrutura envolve provisionamento e ajustes, portanto <strong>não é instantâneo</strong>. Monitoramos a plataforma continuamente e agimos o quanto antes, mas não garantimos prazo de atendimento, tempo de resposta, velocidade de processamento nem disponibilidade ininterrupta, salvo se acordado por escrito em contrato específico.

Se a sua operação já está em volume muito elevado, fale com a gente antes de migrar para avaliarmos juntos o dimensionamento adequado.`;

export const BILLING_EXPLANATION = `Ao migrar para um novo plano, você será redirecionado ao <strong>Mercado Pago</strong> para confirmar a assinatura mensal recorrente.

Nos planos <strong>Crescendo</strong> e <strong>Profissional</strong> você tem <strong>7 dias grátis</strong> para testar. No plano <strong>Ilimitado</strong> a cobrança é imediata, sem período de teste.

Após a confirmação no Mercado Pago, o plano é liberado automaticamente na sua conta. Você pode acompanhar pagamentos em "Minha assinatura".`;

export const SCROLL_DURATION_MS = 1000;

export async function startSubscriptionCheckoutAfterMigrate(migrateData, onSuccess) {
    try {
        const checkoutResponse = await api.post('/me/subscription/checkout');
        const checkoutData = checkoutResponse?.data?.data;
        if (checkoutData?.exempt) {
            await Swal.fire({
                icon: 'success',
                title: 'Plano ativado',
                text: 'Sua conta possui isenção de cobrança. O plano já está ativo.',
            });
            onSuccess?.(migrateData);
            return;
        }
        if (checkoutData?.checkout_url) {
            window.location.href = checkoutData.checkout_url;
            return;
        }
        throw new Error('Checkout indisponível.');
    } catch (error) {
        const message = error?.response?.data?.message
            || error?.response?.data?.error
            || error?.message
            || 'Não foi possível iniciar o checkout.';
        await Swal.fire({
            icon: 'error',
            title: 'Falha no checkout',
            text: message,
        });
        onSuccess?.(migrateData);
    }
}

function isWindowScrollContainer(container) {
    return !container
        || container === document.documentElement
        || container === document.body
        || container === document.scrollingElement;
}

function getScrollContainer(element) {
    let node = element?.parentElement;
    while (node && node !== document.body && node !== document.documentElement) {
        const { overflowY } = window.getComputedStyle(node);
        if ((overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay')
            && node.scrollHeight > node.clientHeight + 1) {
            return node;
        }
        node = node.parentElement;
    }
    return document.scrollingElement || document.documentElement;
}

function readScrollTop(container) {
    if (isWindowScrollContainer(container)) {
        return window.scrollY
            || document.documentElement.scrollTop
            || document.body.scrollTop
            || 0;
    }
    return container.scrollTop;
}

function writeScrollTop(container, value) {
    if (isWindowScrollContainer(container)) {
        window.scrollTo({ top: value, left: 0, behavior: 'auto' });
        return;
    }
    container.scrollTop = value;
}

function computeTargetScrollTop(element, container, offset) {
    if (isWindowScrollContainer(container)) {
        const rect = element.getBoundingClientRect();
        return readScrollTop(container) + rect.top - offset;
    }
    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    return container.scrollTop + (elementRect.top - containerRect.top) - offset;
}

export function smoothScrollToElement(element, duration = SCROLL_DURATION_MS, animationRef) {
    if (!element) {
        return;
    }

    if (animationRef?.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
    }

    const scrollContainer = getScrollContainer(element);
    const offset = 24;
    const startTop = readScrollTop(scrollContainer);
    const targetTop = computeTargetScrollTop(element, scrollContainer, offset);
    const distance = targetTop - startTop;

    if (Math.abs(distance) < 1) {
        return;
    }

    const startTime = performance.now();

    const tick = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        writeScrollTop(scrollContainer, startTop + distance * progress);
        if (progress < 1) {
            animationRef.current = requestAnimationFrame(tick);
        } else {
            animationRef.current = null;
        }
    };

    animationRef.current = requestAnimationFrame(tick);
}

export function formatSubscriptionPlanPrice(monthlyBilledValue) {
    const cents = Number(monthlyBilledValue) || 0;
    if (cents <= 0) {
        return 'Grátis';
    }
    return `${formatCurrencyInCents(cents)}/mês`;
}

export function renderHtmlText(text) {
    if (typeof text === 'string' && /<[^>]+>/.test(text)) {
        return (
            <Box
                component="span"
                sx={{ '& strong': { fontWeight: 700 } }}
                dangerouslySetInnerHTML={{ __html: text }}
            />
        );
    }
    return text;
}

function buildMigrationCheckboxLabels(plan) {
    const planName = plan?.name || 'selecionado';
    const priceLabel = formatCurrencyInCents(Number(plan?.monthly_billed_value) || 0);

    return {
        monthly_charge: `Estou de acordo que será cobrado mensalmente o valor de <strong>${priceLabel}</strong> para manter ativos os recursos do plano <strong>${planName}</strong> que selecionei.`,
        delinquency: 'Estou de acordo que, em caso de inadimplência, <strong>após 5 dias corridos</strong>, perderei acesso aos recursos exclusivos do plano.',
        billing_channels: 'Estou de acordo que a cobrança mensal será feita via <strong>Mercado Pago</strong> (assinatura recorrente) e que poderei acompanhar o status em "Minha assinatura".',
        [UNLIMITED_CAPACITY_KEY]: 'Entendi que <strong>Ilimitado</strong> significa que não haverá limite de quantidade de registros na minha conta, e que isso <strong>não representa garantia de capacidade de processamento, memória, armazenamento ou disponibilidade ininterrupta</strong>. Caso ocorra alguma oscilação de desempenho em momentos de pico, o dimensionamento dos recursos computacionais necessários será trabalhado com a maior celeridade possível.',
    };
}

export function planMonthlyPrice(plan) {
    return Number(plan?.monthly_billed_value ?? plan?.subscription_price) || 0;
}

export function patchAccountFromResponse(data) {
    if (!data?.plan?.name) {
        return;
    }
    patchStoredUser({
        plan_name: data.plan.name,
        plan_migration_pending: Boolean(data.plan_migration_pending),
        pending_plan_name: data.pending_plan?.name || null,
        pending_downgrade_plan_name: data.pending_downgrade_plan?.name || null,
    });
}

/** Cotas em destaque no cartão; o restante fica na comparação completa. */
const HEADLINE_QUOTA_KEYS = ['printers', 'orders', 'jobs', 'customers'];

function PlanHeadlineLimits({ plan }) {
    const rows = HEADLINE_QUOTA_KEYS
        .map((key) => ({ quota: findPlanQuota(key), limit: planLimitFor(plan, key) }))
        .filter((row) => row.quota);

    if (rows.length === 0) {
        return null;
    }

    return (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Limites
            </Typography>
            {rows.map(({ quota, limit }) => (
                <Box key={quota.key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        {quota.label} {planQuotaPeriodLabel(quota.period)}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatPlanLimit(limit)}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

export function SubscriptionPlanCard({
    plan,
    isCurrent,
    isSelected,
    isMigrating,
    isPendingDowngrade,
    isRecommendedUpgrade,
    changeDirection,
    onSelect,
    busy,
}) {
    const features = Array.isArray(plan.features) ? plan.features : [];
    const highlighted = isCurrent || isSelected || isMigrating || isPendingDowngrade || isRecommendedUpgrade;

    return (
        <Card
            variant="outlined"
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderWidth: highlighted ? 2 : 1,
                borderColor: isSelected
                    ? 'success.main'
                    : isRecommendedUpgrade
                        ? 'success.main'
                        : isMigrating || isPendingDowngrade
                            ? 'warning.main'
                            : isCurrent
                                ? 'primary.main'
                                : 'divider',
                boxShadow: highlighted ? 4 : 0,
                bgcolor: isSelected
                    ? (theme) => alpha(theme.palette.success.main, 0.08)
                    : isRecommendedUpgrade
                        ? (theme) => alpha(theme.palette.success.main, 0.06)
                        : isMigrating || isPendingDowngrade
                            ? (theme) => alpha(theme.palette.warning.main, 0.08)
                            : isCurrent
                                ? (theme) => alpha(theme.palette.primary.main, 0.06)
                                : undefined,
            }}
        >
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h5" component="h2">
                        {plan.name}
                    </Typography>
                    {isSelected ? (
                        <Chip color="success" size="small" label="Selecionado" />
                    ) : null}
                    {!isSelected && isMigrating ? (
                        <Chip color="warning" size="small" label="Migrando para este plano" />
                    ) : null}
                    {!isSelected && !isMigrating && isCurrent ? (
                        <Chip color="primary" size="small" label="Plano atual" />
                    ) : null}
                    {!isSelected && !isCurrent && !isMigrating && isPendingDowngrade ? (
                        <Chip color="warning" size="small" label="Regressão agendada" />
                    ) : null}
                    {!isSelected && !isCurrent && !isMigrating && isRecommendedUpgrade ? (
                        <Chip color="success" size="small" label="Desbloqueia este recurso" />
                    ) : null}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 48 }}>
                    {plan.description || '—'}
                </Typography>
                <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
                    {formatSubscriptionPlanPrice(plan.monthly_billed_value)}
                </Typography>
                <List dense disablePadding sx={{ flex: 1 }}>
                    {features.map((feature) => (
                        <ListItem key={feature} disableGutters sx={{ alignItems: 'flex-start' }}>
                            <ListItemIcon sx={{ minWidth: 32, mt: 0.25 }}>
                                <CheckCircleOutlineIcon color="primary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary={feature}
                                primaryTypographyProps={{ variant: 'body2' }}
                            />
                        </ListItem>
                    ))}
                    {features.length === 0 ? (
                        <ListItem disableGutters>
                            <ListItemText primary="Sem funcionalidades listadas." />
                        </ListItem>
                    ) : null}
                </List>
                <PlanHeadlineLimits plan={plan} />
            </CardContent>
            {!isCurrent && changeDirection && !isMigrating && !isPendingDowngrade ? (
                <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                    <Button
                        fullWidth
                        variant={changeDirection === 'downgrade' ? 'outlined' : 'contained'}
                        color={changeDirection === 'downgrade' ? 'warning' : isRecommendedUpgrade ? 'success' : 'primary'}
                        disabled={busy}
                        onClick={() => onSelect(plan)}
                    >
                        {changeDirection === 'downgrade'
                            ? 'Regredir para este plano'
                            : 'Migrar para este plano'}
                    </Button>
                </CardActions>
            ) : null}
        </Card>
    );
}

const FARM_LINK_EXCLUSIVE_QUESTION = 'Opa... preciso confirmar uma coisa antes. Você está querendo assinar o plano Pro exclusivamente por conta da conectividade com as impressoras em tempo real?';

const FARM_LINK_SECONDARY_QUESTION = 'Você pretende utilizar essa funcionalidade de conectividade com impressoras, mesmo que não seja o principal motivo da sua escolha?';

function formatPrinterModelLabel(item) {
    const brand = String(item?.brand || '').trim();
    const model = String(item?.model || '').trim();
    if (brand && model) {
        return `${brand} ${model}`;
    }
    return brand || model || '—';
}

function FarmLinkAnswerChip({ label }) {
    return (
        <Chip
            label={label}
            size="small"
            color="primary"
            variant="outlined"
            icon={<CheckIcon sx={{ fontSize: 16 }} />}
            sx={{ fontWeight: 600 }}
        />
    );
}

function FarmLinkQuestionCard({ answered, answerLabel, onChangeAnswer, children, actions }) {
    return (
        <Box
            sx={(theme) => ({
                p: { xs: 2, sm: 2.5 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: answered ? alpha(theme.palette.success.main, 0.45) : theme.palette.divider,
                bgcolor: answered ? alpha(theme.palette.success.main, 0.05) : theme.palette.background.paper,
                boxShadow: answered ? 'none' : `0 1px 3px ${alpha(theme.palette.common.black, 0.06)}`,
                transition: 'border-color 0.25s ease, background-color 0.25s ease, box-shadow 0.25s ease',
            })}
        >
            <Typography variant="body1" sx={{ mb: answered ? 1.5 : 2, maxWidth: 640, lineHeight: 1.6 }}>
                {children}
            </Typography>
            {answered ? (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <FarmLinkAnswerChip label={answerLabel} />
                    <Button size="small" variant="text" onClick={onChangeAnswer} sx={{ minWidth: 0, px: 1 }}>
                        Alterar
                    </Button>
                </Stack>
            ) : (
                actions
            )}
        </Box>
    );
}

function ProFarmLinkGate({ onBack, onProceed, intent = 'subscription' }) {
    const scrollAnimRef = useRef(null);
    const secondaryStepRef = useRef(null);
    const printersStepRef = useRef(null);
    const skipStepRef = useRef(null);

    const [exclusiveAnswer, setExclusiveAnswer] = useState(null);
    const [secondaryAnswer, setSecondaryAnswer] = useState(null);
    const [supportedModels, setSupportedModels] = useState([]);
    const [loadingModels, setLoadingModels] = useState(false);
    const [modelsError, setModelsError] = useState('');

    const showSecondary = exclusiveAnswer === 'no';
    const showPrinters = exclusiveAnswer === 'yes' || secondaryAnswer === 'yes';
    const showSkipStep = exclusiveAnswer === 'no' && secondaryAnswer === 'no';
    const proceedMode = exclusiveAnswer === 'yes' ? 'exclusive' : 'not_primary';

    const loadSupportedModels = useCallback(async () => {
        setLoadingModels(true);
        setModelsError('');
        try {
            const response = await listFarmLinkSupportedPrinterModels();
            const items = response?.data?.items || [];
            setSupportedModels(Array.isArray(items) ? items : []);
        } catch {
            setModelsError('Não foi possível carregar a lista de impressoras compatíveis.');
            setSupportedModels([]);
        } finally {
            setLoadingModels(false);
        }
    }, []);

    useEffect(() => {
        if (!showPrinters) {
            return undefined;
        }
        loadSupportedModels();
        return undefined;
    }, [showPrinters, loadSupportedModels]);

    useEffect(() => {
        if (intent === 'registration') {
            return undefined;
        }
        const target = showSkipStep
            ? skipStepRef.current
            : showPrinters
                ? printersStepRef.current
                : showSecondary
                    ? secondaryStepRef.current
                    : null;
        if (!target) {
            return undefined;
        }
        const timer = window.setTimeout(() => {
            smoothScrollToElement(target, SCROLL_DURATION_MS, scrollAnimRef);
        }, 280);
        return () => window.clearTimeout(timer);
    }, [intent, showSecondary, showPrinters, showSkipStep]);

    const resetFromExclusive = () => {
        setExclusiveAnswer(null);
        setSecondaryAnswer(null);
        setSupportedModels([]);
        setModelsError('');
    };

    const resetFromSecondary = () => {
        setSecondaryAnswer(null);
        setSupportedModels([]);
        setModelsError('');
    };

    const answerButtons = (onAnswer) => (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="contained" onClick={() => onAnswer('yes')} sx={{ minWidth: 96 }}>
                Sim
            </Button>
            <Button variant="outlined" onClick={() => onAnswer('no')} sx={{ minWidth: 96 }}>
                Não
            </Button>
        </Stack>
    );

    const timelineDotSx = {
        boxShadow: 'none',
        width: 36,
        height: 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        my: 0.5,
    };

    const stableModels = useMemo(
        () => supportedModels
            .filter((item) => !item.experimental)
            .sort((a, b) => formatPrinterModelLabel(a).localeCompare(formatPrinterModelLabel(b), 'pt-BR')),
        [supportedModels],
    );

    const experimentalModels = useMemo(
        () => supportedModels
            .filter((item) => Boolean(item.experimental))
            .sort((a, b) => formatPrinterModelLabel(a).localeCompare(formatPrinterModelLabel(b), 'pt-BR')),
        [supportedModels],
    );

    const proceedExclusiveLabel = intent === 'registration'
        ? 'Quero o plano Pro'
        : 'Sim, desejo prosseguir';
    const proceedNotPrimaryLabel = intent === 'registration'
        ? 'Quero o plano Pro'
        : 'Quero prosseguir, esse não é o principal motivo da minha escolha';
    const proceedSkipLabel = intent === 'registration'
        ? 'Continuar para criar conta'
        : 'Continuar para confirmação do plano';
    const skipBodyText = intent === 'registration'
        ? 'Entendido — você pode seguir com o cadastro no plano Pro normalmente.'
        : 'Entendido — você pode seguir com a assinatura do plano Pro normalmente.';

    return (
        <Paper
            variant="outlined"
            sx={(theme) => ({
                p: { xs: 2.5, sm: 3 },
                mb: 3,
                borderRadius: 2,
                background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${theme.palette.background.paper} 120px)`,
            })}
        >
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 3 }}>
                <Box
                    sx={(theme) => ({
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        color: 'primary.main',
                        flexShrink: 0,
                    })}
                >
                    <PrecisionManufacturingOutlinedIcon />
                </Box>
                <Box>
                    <Typography variant="h5" sx={{ mb: 0.5 }}>
                        Antes de continuar
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
                        O plano Pro inclui o Farm Link — conectividade em tempo real com impressoras.
                        Precisamos alinhar expectativas antes de seguir.
                    </Typography>
                </Box>
            </Stack>

            <Timeline
                position="right"
                sx={{
                    p: 0,
                    m: 0,
                    maxWidth: 760,
                    [`& .MuiTimelineItem-root:before`]: { flex: 0, padding: 0 },
                }}
            >
                <TimelineItem>
                    <TimelineSeparator>
                        <TimelineDot
                            color={exclusiveAnswer ? 'success' : 'primary'}
                            variant={exclusiveAnswer ? 'filled' : 'outlined'}
                            sx={timelineDotSx}
                        >
                            {exclusiveAnswer ? <CheckIcon sx={{ fontSize: 18 }} /> : <HelpOutlineIcon sx={{ fontSize: 18 }} />}
                        </TimelineDot>
                        {(showSecondary || showPrinters || showSkipStep) ? (
                            <TimelineConnector sx={{ bgcolor: exclusiveAnswer ? 'success.light' : 'divider' }} />
                        ) : null}
                    </TimelineSeparator>
                    <TimelineContent sx={{ py: 0, px: { xs: 1.5, sm: 2 }, pb: showSecondary || showPrinters || showSkipStep ? 3 : 0 }}>
                        <FarmLinkQuestionCard
                            answered={Boolean(exclusiveAnswer)}
                            answerLabel={exclusiveAnswer === 'yes' ? 'Sim, é o motivo principal' : 'Não é o motivo principal'}
                            onChangeAnswer={resetFromExclusive}
                            actions={answerButtons(setExclusiveAnswer)}
                        >
                            {FARM_LINK_EXCLUSIVE_QUESTION}
                        </FarmLinkQuestionCard>
                    </TimelineContent>
                </TimelineItem>

                <Collapse in={showSecondary} timeout={400} unmountOnExit>
                    <TimelineItem ref={secondaryStepRef}>
                        <TimelineSeparator>
                            <TimelineDot
                                color={secondaryAnswer ? 'success' : 'primary'}
                                variant={secondaryAnswer ? 'filled' : 'outlined'}
                                sx={timelineDotSx}
                            >
                                {secondaryAnswer ? <CheckIcon sx={{ fontSize: 18 }} /> : <HelpOutlineIcon sx={{ fontSize: 18 }} />}
                            </TimelineDot>
                            {(showPrinters || showSkipStep) ? (
                                <TimelineConnector sx={{ bgcolor: secondaryAnswer ? 'success.light' : 'divider' }} />
                            ) : null}
                        </TimelineSeparator>
                        <TimelineContent sx={{ py: 0, px: { xs: 1.5, sm: 2 }, pb: showPrinters || showSkipStep ? 3 : 0 }}>
                            <Fade in={showSecondary} timeout={500}>
                                <Box>
                                    <FarmLinkQuestionCard
                                        answered={Boolean(secondaryAnswer)}
                                        answerLabel={secondaryAnswer === 'yes' ? 'Sim, vou utilizar' : 'Não vou utilizar'}
                                        onChangeAnswer={resetFromSecondary}
                                        actions={answerButtons(setSecondaryAnswer)}
                                    >
                                        {FARM_LINK_SECONDARY_QUESTION}
                                    </FarmLinkQuestionCard>
                                </Box>
                            </Fade>
                        </TimelineContent>
                    </TimelineItem>
                </Collapse>

                <Collapse in={showPrinters} timeout={400} unmountOnExit>
                    <TimelineItem ref={printersStepRef}>
                        <TimelineSeparator>
                            <TimelineDot color="warning" variant="filled" sx={timelineDotSx}>
                                <PrecisionManufacturingOutlinedIcon sx={{ fontSize: 18 }} />
                            </TimelineDot>
                        </TimelineSeparator>
                        <TimelineContent sx={{ py: 0, px: { xs: 1.5, sm: 2 } }}>
                            <Fade in={showPrinters} timeout={500}>
                                <Box
                                    sx={(theme) => ({
                                        p: { xs: 2, sm: 2.5 },
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: alpha(theme.palette.warning.main, 0.35),
                                        bgcolor: alpha(theme.palette.warning.main, 0.06),
                                    })}
                                >
                                    <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600 }}>
                                        Impressoras compatíveis com Farm Link
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 640 }}>
                                        Atualmente o Farm Link oferece suporte às seguintes impressoras:
                                    </Typography>

                                    {loadingModels ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                            <CircularProgress size={28} />
                                        </Box>
                                    ) : null}

                                    {!loadingModels && modelsError ? (
                                        <Alert severity="warning" sx={{ mb: 2 }}>
                                            {modelsError}
                                        </Alert>
                                    ) : null}

                                    {!loadingModels && !modelsError && supportedModels.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            Nenhum modelo cadastrado como compatível no momento.
                                        </Typography>
                                    ) : null}

                                    {!loadingModels && supportedModels.length > 0 ? (
                                        <Stack spacing={2} sx={{ mb: 2.5 }}>
                                            {/* Seção 1: Modelos com integração oficial / estável */}
                                            <Box>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: 'text.primary',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.75,
                                                        mb: 1,
                                                    }}
                                                >
                                                    <PrecisionManufacturingOutlinedIcon fontSize="small" color="primary" />
                                                    Modelos com integração oficial:
                                                </Typography>
                                                {stableModels.length > 0 ? (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                        {stableModels.map((item) => (
                                                            <Chip
                                                                key={item.uuid || item.id || formatPrinterModelLabel(item)}
                                                                icon={<PrecisionManufacturingOutlinedIcon />}
                                                                label={formatPrinterModelLabel(item)}
                                                                variant="outlined"
                                                                size="small"
                                                                sx={{ bgcolor: 'background.paper', fontWeight: 500 }}
                                                            />
                                                        ))}
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                        Nenhum modelo nesta categoria no momento.
                                                    </Typography>
                                                )}
                                            </Box>

                                            {/* Seção 2: Modelos em fase experimental */}
                                            <Box
                                                sx={(theme) => ({
                                                    p: 1.5,
                                                    borderRadius: 1.5,
                                                    border: '1px solid',
                                                    borderColor: alpha(theme.palette.warning.main, 0.4),
                                                    bgcolor: alpha(theme.palette.warning.main, 0.08),
                                                })}
                                            >
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: 'warning.dark',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.75,
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    <ScienceOutlinedIcon fontSize="small" color="warning" />
                                                    Modelos com integração experimental (em testes):
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                                                    O suporte a esses modelos ainda está sendo validado e refinado, podendo sofrer ajustes ou instabilidades.
                                                </Typography>
                                                {experimentalModels.length > 0 ? (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                        {experimentalModels.map((item) => (
                                                            <Chip
                                                                key={item.uuid || item.id || formatPrinterModelLabel(item)}
                                                                icon={<ScienceOutlinedIcon />}
                                                                label={formatPrinterModelLabel(item)}
                                                                color="warning"
                                                                variant="outlined"
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: 'background.paper',
                                                                    borderColor: 'warning.main',
                                                                    fontWeight: 600,
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                        Nenhum modelo experimental no momento.
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Stack>
                                    ) : null}

                                    <Button variant="contained" onClick={onProceed} fullWidth={false}>
                                        {proceedMode === 'exclusive'
                                            ? proceedExclusiveLabel
                                            : proceedNotPrimaryLabel}
                                    </Button>
                                </Box>
                            </Fade>
                        </TimelineContent>
                    </TimelineItem>
                </Collapse>

                <Collapse in={showSkipStep} timeout={400} unmountOnExit>
                    <TimelineItem ref={skipStepRef}>
                        <TimelineSeparator>
                            <TimelineDot color="success" variant="filled" sx={timelineDotSx}>
                                <CheckIcon sx={{ fontSize: 18 }} />
                            </TimelineDot>
                        </TimelineSeparator>
                        <TimelineContent sx={{ py: 0, px: { xs: 1.5, sm: 2 } }}>
                            <Fade in={showSkipStep} timeout={500}>
                                <Box
                                    sx={(theme) => ({
                                        p: { xs: 2, sm: 2.5 },
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: alpha(theme.palette.success.main, 0.35),
                                        bgcolor: alpha(theme.palette.success.main, 0.06),
                                    })}
                                >
                                    <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600 }}>
                                        Tudo certo
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 640 }}>
                                        {skipBodyText}
                                    </Typography>
                                    <Button variant="contained" onClick={onProceed}>
                                        {proceedSkipLabel}
                                    </Button>
                                </Box>
                            </Fade>
                        </TimelineContent>
                    </TimelineItem>
                </Collapse>
            </Timeline>

            <Stack direction="row" justifyContent="flex-start" sx={{ mt: 2, pl: { xs: 0, sm: 7 } }}>
                <Button variant="text" onClick={onBack}>
                    Voltar aos planos
                </Button>
            </Stack>
        </Paper>
    );
}

export { ProFarmLinkGate };

export function UpgradeConfirmation({ plan, onBack, onSuccess }) {
    const requiresFarmLinkGate = isProSubscriptionPlan(plan);
    const [farmLinkCleared, setFarmLinkCleared] = useState(!requiresFarmLinkGate);

    if (requiresFarmLinkGate && !farmLinkCleared) {
        return (
            <ProFarmLinkGate
                onBack={onBack}
                onProceed={() => setFarmLinkCleared(true)}
            />
        );
    }

    return (
        <MigrationConfirmation
            plan={plan}
            onBack={onBack}
            onSuccess={onSuccess}
        />
    );
}

export function MigrationConfirmation({ plan, onBack, onSuccess }) {
    const checkboxKeys = useMemo(() => migrationCheckboxKeysFor(plan), [plan]);
    const [checked, setChecked] = useState(() => (
        Object.fromEntries(checkboxKeys.map((key) => [key, false]))
    ));
    const [migrating, setMigrating] = useState(false);

    const checkboxLabels = useMemo(() => buildMigrationCheckboxLabels(plan), [plan]);
    const allChecked = checkboxKeys.every((key) => checked[key]);
    const showCapacityNotice = isUnlimitedSubscriptionPlan(plan);

    const handleToggle = (key) => (event) => {
        setChecked((prev) => ({ ...prev, [key]: event.target.checked }));
    };

    const handleMigrate = async () => {
        if (!allChecked || migrating) {
            return;
        }

        const result = await Swal.fire({
            icon: 'question',
            title: 'Confirmar migração',
            text: `Deseja migrar para o plano ${plan.name}? Você será redirecionado ao Mercado Pago para confirmar a assinatura.`,
            showCancelButton: true,
            confirmButtonText: 'Sim, continuar',
            cancelButtonText: 'Cancelar',
        });

        if (!result.isConfirmed) {
            return;
        }

        setMigrating(true);
        try {
            const response = await api.post('/me/plan/migrate', { plan_uuid: plan.uuid });
            const data = response?.data?.data;
            patchAccountFromResponse(data);
            await fetchFeatureFlags();
            await startSubscriptionCheckoutAfterMigrate(data, onSuccess);
        } catch (error) {
            const message = error?.response?.data?.error
                || error?.response?.data?.message
                || 'Não foi possível migrar o plano. Tente novamente.';
            await Swal.fire({
                icon: 'error',
                title: 'Falha na migração',
                text: message,
            });
        } finally {
            setMigrating(false);
        }
    };

    return (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
                Migrar para o plano {plan.name}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, whiteSpace: 'pre-line', maxWidth: 720 }}>
                {renderHtmlText(BILLING_EXPLANATION)}
            </Typography>

            {showCapacityNotice ? (
                <Alert severity="info" sx={{ mb: 3, maxWidth: 720 }}>
                    <AlertTitle>Sobre o plano Ilimitado</AlertTitle>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                        {renderHtmlText(UNLIMITED_CAPACITY_EXPLANATION)}
                    </Typography>
                </Alert>
            ) : null}

            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Confirme os termos abaixo
            </Typography>
            <FormGroup sx={{ mb: 3 }}>
                {checkboxKeys.map((key) => (
                    <FormControlLabel
                        key={key}
                        control={(
                            <Checkbox
                                checked={Boolean(checked[key])}
                                onChange={handleToggle(key)}
                                disabled={migrating}
                            />
                        )}
                        label={renderHtmlText(checkboxLabels[key])}
                        sx={{ alignItems: 'flex-start', mb: 1 }}
                    />
                ))}
            </FormGroup>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="outlined" onClick={onBack} disabled={migrating}>
                    Voltar aos planos
                </Button>
                <Button
                    variant="contained"
                    disabled={!allChecked || migrating}
                    onClick={handleMigrate}
                >
                    {migrating ? 'Redirecionando…' : 'Confirmar e pagar'}
                </Button>
            </Box>
        </Paper>
    );
}

export function DowngradeConfirmation({
    plan,
    currentPlanName,
    renewalDateLabel,
    onBack,
    onSuccess,
}) {
    const [accepted, setAccepted] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const renewalText = renewalDateLabel
        ? `até ${renewalDateLabel}`
        : 'até a data da próxima cobrança';

    const handleConfirm = async () => {
        if (!accepted || submitting) {
            return;
        }

        const result = await Swal.fire({
            icon: 'warning',
            title: 'Confirmar regressão de plano',
            html: `Seu plano atual <strong>${currentPlanName}</strong> permanecerá ativo ${renewalText}. Após essa data, sua assinatura será alterada para o plano <strong>${plan.name}</strong>.`,
            showCancelButton: true,
            confirmButtonText: 'Sim, regredir plano',
            cancelButtonText: 'Cancelar',
        });

        if (!result.isConfirmed) {
            return;
        }

        setSubmitting(true);
        try {
            const response = await api.post('/me/plan/migrate', { plan_uuid: plan.uuid });
            const data = response?.data?.data;
            patchAccountFromResponse(data);
            await fetchFeatureFlags();
            await Swal.fire({
                icon: 'success',
                title: 'Regressão agendada',
                html: `Seu plano <strong>${currentPlanName}</strong> continua ativo ${renewalText}. Depois disso, você passará para o plano <strong>${plan.name}</strong>.`,
            });
            onSuccess(data);
        } catch (error) {
            const message = error?.response?.data?.error
                || error?.response?.data?.message
                || 'Não foi possível agendar a regressão. Tente novamente.';
            await Swal.fire({
                icon: 'error',
                title: 'Falha na regressão',
                text: message,
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
                Regredir para o plano {plan.name}
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
                Seu plano atual <strong>{currentPlanName}</strong> permanecerá ativo {renewalText}.
                {' '}Após essa data, sua assinatura será alterada para o plano <strong>{plan.name}</strong>.
            </Alert>

            <FormGroup sx={{ mb: 3 }}>
                <FormControlLabel
                    control={(
                        <Checkbox
                            checked={accepted}
                            onChange={(event) => setAccepted(event.target.checked)}
                            disabled={submitting}
                        />
                    )}
                    label={`Entendo que continuarei com os recursos do plano ${currentPlanName} ${renewalText} e só depois passarei para o plano ${plan.name}.`}
                    sx={{ alignItems: 'flex-start' }}
                />
            </FormGroup>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="outlined" onClick={onBack} disabled={submitting}>
                    Voltar aos planos
                </Button>
                <Button
                    variant="contained"
                    color="warning"
                    disabled={!accepted || submitting}
                    onClick={handleConfirm}
                >
                    {submitting ? 'Agendando…' : 'Confirmar regressão'}
                </Button>
            </Box>
        </Paper>
    );
}
